import React, { Component } from 'react';
import NavigationPrompt from 'react-router-navigation-prompt';
import { getFormValues } from 'redux-form';
import { Button } from 'primereact/button';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Dialog } from 'primereact/dialog';
import { defineMessages, injectIntl } from 'react-intl';
import DimensionsEditButtons from './DimensionsEditButtons';
import BehaviorBenchmarks from './BehaviorBenchmarks';
import CognitiveBenchmarks from './CognitiveBenchmarks';
import InterestsBenchmarks from './InterestsBenchmarks';
import ArrayUtil from '../../../utils/ArrayUtil';
import { withAccount } from '../../../components/AccountContext';
import { growlActionSuccess, growlErrors } from '../../../utils/NotificationUtil';
import { withGrowl } from '../../../components/GrowlNotifications';
import { growlShape } from '../../../components/prop-types';

import { updateJobProfileDimension } from '../../../services/apps/JobProfileService';
import UpdateJobProfileDimensionBenchmarksConfirmationDialog from './UpdateJobProfileDimensionBenchmarksConfirmationDialog';

const mapStateToProps = (state) => ({
  jobProfileEditDimensions: getFormValues('jobProfileEditDimensions')(state),
});

const messages = defineMessages({
  yes: { id: 'btn.lbl.yes', defaultMessage: 'Yes' },
  edit: { id: 'actions.edit', defaultMessage: 'Edit' },
  no: { id: 'btn.lbl.no', defaultMessage: 'No' },
  header: { id: 'modal.navigation.away.header', defaultMessage: 'Unsaved Changes' },
  warning: {
    id: 'modal.navigation.away.warning',
    defaultMessage: 'Are you sure you want to leave this page? You have unsaved changes.',
  },
  successNoti: {
    id: 'noti.success',
    defaultMessage: 'Success!',
  },
});

const footer = (onConfirm, onCancel, formatMessage) => (
  <div>
    <Button label={formatMessage(messages.yes)} icon="pi pi-check" onClick={onConfirm} />
    <Button label={formatMessage(messages.no)} icon="pi pi-times" onClick={onCancel} className="p-button-secondary" />
  </div>
);

const listDimension = (list) => {
  const dimensions = [];
  if (list && ArrayUtil.isNotEmpty(list.dimensionBenchmarks)) {
    list.dimensionBenchmarks.forEach((dimension) =>
      dimensions.push({
        code: dimension.code,
        low: dimension.low,
        high: dimension.high,
      }),
    );
  }
  return dimensions;
};

const listInterestsDimensions = (benchmarks) => {
  const dimensions = [];

  if (benchmarks && ArrayUtil.isNotEmpty(benchmarks.dimensionBenchmarks)) {
    benchmarks.dimensionBenchmarks.forEach((dimension) => {
      dimensions.push({
        code: dimension.code,
        priority: dimension.priority,
      });
    });
  }
  return dimensions;
};

const updateDimensions = (benmarks, suggestions) => {
  if (benmarks) {
    const { dimensionBenchmarks } = benmarks;
    const dimensions = ArrayUtil.isNotEmpty(dimensionBenchmarks)
      ? dimensionBenchmarks.map((dim) => {
          const updatedDim = suggestions.find((suggest) => dim.code === suggest.code);
          return {
            ...updatedDim,
            displayName: dim.displayName,
          };
        })
      : [];
    return dimensions;
  }
  return [];
};

class JobProfileDetailDimensions extends Component {
  constructor(props) {
    super(props);

    const { jobProfile } = this.props;
    const { dimensionBenchmarks } = jobProfile;

    this.state = {
      isSaving: false,
      isProcessing: false,
      previousSuggestion: null,
      behaviorBenchmarks: dimensionBenchmarks.behaviorBenchmarks,
      cognitiveBenchmarks: dimensionBenchmarks.cognitiveBenchmarks,
      interestsBenchmarks: dimensionBenchmarks.interestsBenchmarks,
      confirmationDialogVisible: false,
    };

    this.handleDimensionsEdit = this.handleDimensionsEdit.bind(this);
    this.toggleEdit = this.toggleEdit.bind(this);
    this.resetBenchMarks = this.resetBenchMarks.bind(this);
    this.handleSubmitEdit = this.handleSubmitEdit.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleDimensionRangeChange = this.handleDimensionRangeChange.bind(this);
    this.handleInterestPriorityChange = this.handleInterestPriorityChange.bind(this);
    this.showConfirmationDialog = this.showConfirmationDialog.bind(this);
    this.hideConfirmationDialog = this.hideConfirmationDialog.bind(this);
  }

  componentWillReceiveProps(props) {
    this.updateDimensionBySuggestion(props);
  }

  async handleDimensionsEdit(behaviorBenchmarks, cognitiveBenchmarks, interestsBenchmarks) {
    const { jobProfile } = this.props;

    const { id } = jobProfile;
    const { growl, currentAccount, intl } = this.props;
    const { formatMessage } = intl;

    try {
      await updateJobProfileDimension({
        jobProfileId: id,
        behaviorBenchmarks,
        cognitiveBenchmarks,
        interestsBenchmarks,
        accountId: currentAccount,
      });
      growlActionSuccess(growl, formatMessage(messages.successNoti));
      this.resetBenchMarks();
      this.toggleEdit();
    } catch (errors) {
      growlErrors(growl, errors);
    }
  }

  updateDimensionBySuggestion(props) {
    const { suggestions } = props;
    const { previousSuggestion } = this.state;
    const { behaviorBenchmarks, cognitiveBenchmarks, interestsBenchmarks } = this.state;
    if (suggestions && suggestions.data && (!previousSuggestion || previousSuggestion !== suggestions.timestamp)) {
      const {
        data: { dimensionSuggestionBehavior, dimensionSuggestionCognitive, dimensionSuggestionInterest },
        timestamp,
      } = suggestions;

      const updatedBehavior = updateDimensions(behaviorBenchmarks, dimensionSuggestionBehavior);
      const updatedCognitive = updateDimensions(cognitiveBenchmarks, dimensionSuggestionCognitive);
      const updatedInterest = updateDimensions(interestsBenchmarks, dimensionSuggestionInterest);
      const sortedInterest = ArrayUtil.sortBenchMarksPriority(updatedInterest);

      this.setState({
        previousSuggestion: timestamp,
        behaviorBenchmarks: {
          ...behaviorBenchmarks,
          dimensionBenchmarks: updatedBehavior,
        },
        cognitiveBenchmarks: {
          ...cognitiveBenchmarks,
          dimensionBenchmarks: updatedCognitive,
        },
        interestsBenchmarks: {
          ...interestsBenchmarks,
          dimensionBenchmarks: sortedInterest,
        },
      });
    }
  }

  toggleEdit() {
    const { toggleEdit } = this.props;
    toggleEdit();
  }

  resetBenchMarks() {
    const { jobProfile } = this.props;
    const haveData = jobProfile && jobProfile.dimensionBenchmarks;
    if (haveData) {
      const { dimensionBenchmarks } = jobProfile;
      this.setState({
        behaviorBenchmarks: dimensionBenchmarks.behaviorBenchmarks,
        cognitiveBenchmarks: dimensionBenchmarks.cognitiveBenchmarks,
        interestsBenchmarks: dimensionBenchmarks.interestsBenchmarks,
      });
    }
  }

  handleDimensionRangeChange(newHigh, newLow, code, sectionCode) {
    // eslint-disable-next-line react/destructuring-assignment
    const sectionBenchmarks = Object.assign({}, this.state[sectionCode]);
    const dimensionBenchmarks = sectionBenchmarks.dimensionBenchmarks.slice();
    const index = dimensionBenchmarks.findIndex((v) => v.code === code);
    const dimensionRange = Object.assign({}, dimensionBenchmarks[index]);

    dimensionRange.low = newLow;
    dimensionRange.high = newHigh;
    dimensionBenchmarks[index] = dimensionRange;
    sectionBenchmarks.dimensionBenchmarks = dimensionBenchmarks;

    if (!dimensionRange.distortion) {
      this.setState({ [sectionCode]: sectionBenchmarks });
    }
  }

  handleInterestPriorityChange(interestsBenchmarks) {
    this.setState({
      interestsBenchmarks,
    });
  }

  showConfirmationDialog() {
    this.setState({
      showConfirmationDialog: true,
    });
  }

  hideConfirmationDialog() {
    this.setState({
      showConfirmationDialog: false,
    });
  }

  handleCancel() {
    this.resetBenchMarks();
    this.toggleEdit();
  }

  async handleSubmitEdit() {
    const {
      interestsBenchmarks: oldInterest,
      cognitiveBenchmarks: oldCognitive,
      behaviorBenchmarks: oldBehavior,
    } = this.state;
    const behaviorBenchmarks = listDimension(oldBehavior);
    const cognitiveBenchmarks = listDimension(oldCognitive);
    const interestsBenchmarks = listInterestsDimensions(oldInterest);

    await this.handleDimensionsEdit(behaviorBenchmarks, cognitiveBenchmarks, interestsBenchmarks);
  }

  render() {
    const { jobProfile, dataLoading } = this.props;
    const { hintsCS, hintsJPS, hintsSJL, isEdit, currentAccount } = this.props;
    const { dimensionBenchmarks } = jobProfile;
    const { distortionBenchmark } = dimensionBenchmarks;
    const { behaviorBenchmarks, cognitiveBenchmarks, interestsBenchmarks } = this.state;

    if (!jobProfile) {
      return null;
    }
    const { dimensions } = jobProfile.assessmentTemplate;
    let templateForInterest = [];
    if (dimensions && interestsBenchmarks) {
      templateForInterest = dimensions.filter((d) => d.sectionName === interestsBenchmarks.code);
    }
    const { isProcessing } = this.state;
    const {
      intl: { formatMessage },
    } = this.props;
    const detailUrl = `/accounts/${currentAccount}/assessments/templates/${jobProfile.assessmentTemplate.id}`;
    return (
      <>
        <NavigationPrompt
          renderIfNotActive
          when={(crntLocation, nextLocation) =>
            isEdit && (!nextLocation || !nextLocation.pathname.startsWith(crntLocation.pathname))
          }
        >
          {({ isActive, onCancel, onConfirm }) => {
            if (isActive) {
              return (
                <Dialog
                  header={formatMessage(messages.header)}
                  visible={isActive}
                  style={{ width: '50vw' }}
                  modal
                  footer={footer(onConfirm, onCancel, formatMessage)}
                  onHide={() => this.setState({ visible: false })}
                >
                  {formatMessage(messages.warning)}
                </Dialog>
              );
            }
            return null;
          }}
        </NavigationPrompt>

        <UpdateJobProfileDimensionBenchmarksConfirmationDialog
          visible={this.state.showConfirmationDialog}
          confirm={() => {
            this.handleSubmitEdit();
            this.hideConfirmationDialog();
          }}
          hide={this.hideConfirmationDialog}
        />

        <div className="p-grid">
          <div className="p-col-6">
            <Link className="card-title" to={detailUrl}>
              {jobProfile.assessmentTemplate.title}
            </Link>
          </div>

          <div className="p-col-6 align-right">
            {isEdit ? (
              <DimensionsEditButtons
                submitDisabled={isProcessing || dataLoading}
                onSave={() => {
                  if (
                    this.state.behaviorBenchmarks !== dimensionBenchmarks.behaviorBenchmarks ||
                    this.state.cognitiveBenchmarks !== dimensionBenchmarks.cognitiveBenchmarks ||
                    this.state.interestsBenchmarks !== dimensionBenchmarks.interestsBenchmarks
                  ) {
                    this.showConfirmationDialog();
                  } else {
                    this.handleSubmitEdit();
                  }
                }}
                onCancel={this.handleCancel}
              />
            ) : (
              <Button
                icon="fa fa-pencil"
                type="button"
                className="p-button-secondary"
                onClick={this.toggleEdit}
                tooltip={formatMessage(messages.edit)}
                tooltipOptions={{ position: 'bottom' }}
              />
            )}
          </div>
          <div className="p-col-12">
            <BehaviorBenchmarks
              benchmarks={behaviorBenchmarks}
              distortionBenchmark={distortionBenchmark}
              hintsCS={hintsCS}
              hintsJPS={hintsJPS}
              hintsSJL={hintsSJL}
              isEdit={isEdit}
              onChange={this.handleDimensionRangeChange}
            />

            <CognitiveBenchmarks
              benchmarks={cognitiveBenchmarks}
              hintsCS={hintsCS}
              hintsJPS={hintsJPS}
              hintsSJL={hintsSJL}
              isEdit={isEdit}
              onChange={this.handleDimensionRangeChange}
            />

            <InterestsBenchmarks
              benchmarks={interestsBenchmarks}
              templates={templateForInterest}
              hintsCS={hintsCS}
              hintsJPS={hintsJPS}
              hintsSJL={hintsSJL}
              isEdit={isEdit}
              onChange={this.handleInterestPriorityChange}
            />
          </div>
        </div>
      </>
    );
  }
}

JobProfileDetailDimensions.propTypes = {
  intl: PropTypes.objectOf(PropTypes.any).isRequired,
  currentAccount: PropTypes.string.isRequired,
  growl: growlShape.isRequired,
  dataLoading: PropTypes.bool.isRequired,
  hintsCS: PropTypes.objectOf(PropTypes.any),
  hintsJPS: PropTypes.objectOf(PropTypes.any),
  hintsSJL: PropTypes.objectOf(PropTypes.any),
  isEdit: PropTypes.bool.isRequired,
  jobProfile: PropTypes.objectOf(PropTypes.any).isRequired,
  toggleEdit: PropTypes.func.isRequired,
};

JobProfileDetailDimensions.defaultProps = {
  hintsCS: {},
  hintsJPS: {},
  hintsSJL: {},
};

export default withGrowl(injectIntl(withAccount(connect(mapStateToProps)(JobProfileDetailDimensions))));
